import { getErrorMessage } from '@/api/errors'
import type { DebouncedWorkflowSchedulePreview } from '@/hooks/useWorkflowSchedules'
import { extractValidationMessages } from '@/lib/validationErrors'
import type { WorkflowScheduleRecurrenceRequest } from '@/types'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { formatInTimeZone } from './scheduleFormat'

const PREVIEW_FAILED_MESSAGE = 'The recurrence could not be previewed.'
const FIX_FIELD_MESSAGE = 'Fix the highlighted field.'

function previewErrorMessage(error: unknown): string {
  const messages = extractValidationMessages(error)
  if (messages.length > 0) return [...new Set(messages)].join(' ')
  return getErrorMessage(error) ?? PREVIEW_FAILED_MESSAGE
}

interface Props {
  request: WorkflowScheduleRecurrenceRequest | null
  preview: DebouncedWorkflowSchedulePreview
  hasFieldErrors: boolean
}

export function ScheduleRecurrencePreview({ request, preview, hasFieldErrors }: Props) {
  const { data, isError, error, isSettling, debouncedRequest } = preview
  const occurrences = data?.occurrences ?? []

  return (
    <div className="rounded-lg border bg-page p-4 space-y-2" data-testid="schedule-preview" aria-live="polite">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">Next 5 occurrences</h3>
        {isSettling && request && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Updating preview" />}
      </div>

      {!request ? (
        <p className="text-xs text-muted-foreground">Complete the recurrence to see when it fires.</p>
      ) : hasFieldErrors ? (
        <p className="text-sm text-danger-foreground" data-testid="schedule-preview-field-errors">
          {FIX_FIELD_MESSAGE}
        </p>
      ) : isError && !isSettling ? (
        <p className="text-sm text-danger-foreground" role="alert" data-testid="schedule-preview-error">
          {previewErrorMessage(error)}
        </p>
      ) : occurrences.length === 0 ? (
        !isSettling && <p className="text-xs text-muted-foreground">This recurrence has no upcoming occurrences.</p>
      ) : (
        <ol className="space-y-1.5" aria-label="Upcoming occurrences">
          {occurrences.map(o => (
            <li key={o.utc} className="text-sm" data-testid="schedule-preview-occurrence">
              <span>{formatInTimeZone(o.utc, debouncedRequest?.timeZoneId ?? 'UTC')}</span>
              {o.dstNote && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-warning-foreground" data-testid="schedule-preview-dst-note">
                  <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {o.dstNote}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
